import { BrowserRouter, Route, Routes, Navigate, useSearchParams } from 'react-router-dom'
import { AppShell } from '@/components/AppShell'
import { Overview } from '@/routes/Overview'
import { People } from '@/routes/People'
import { Placeholder } from '@/routes/Placeholder'
import { useOnboarding } from '@/onboarding/OnboardingProvider'
import { Balances } from '@/routes/Balances'
import { Payments } from '@/routes/Payments'
import { AuthPage } from '@/routes/AuthPage'
import { Welcome } from '@/routes/Welcome'
import { Settings } from '@/routes/Settings'
import { ConfigProvider } from '@/lib/ConfigProvider'
import { SessionProvider } from '@/auth/SessionProvider'
import { RequireSession } from '@/auth/RequireSession'
import { OnboardingProvider } from '@/onboarding/OnboardingProvider'
import { ThemeProvider } from '@/theme/ThemeProvider'
import { ErrorBoundary } from '@/components/ErrorBoundary'

/**
 * The journey is: create an account → verify your identity → use the wallet.
 *
 * Two of those boundaries are real and they are not the same boundary:
 *
 *   - **A session** is Ory's answer to *who is this*. `RequireSession` gates on
 *     it, because without it there is nobody to render for.
 *   - **Onboarding** is the institution's answer to *may they hold money*. It
 *     is fail-closed and enforced server-side (invariants 7 and 8). This app
 *     **routes** on it — sending a newly-registered customer to `/welcome`
 *     rather than to a wallet that would refuse everything — but never
 *     *enforces* it. A client-side block would be an authorization decision in
 *     a browser bundle, and would strand anyone whose state we cannot read.
 */
/**
 * What `/` shows a signed-in customer (#81).
 *
 * # The problem this exists for
 *
 * `Overview` renders `src/data/fixtures.ts`. Signed in as an identity the
 * backend answers `403 not_a_customer` for — no wallet, no account, no IBAN —
 * `/` showed four balances and the fixture customer's first name, directly
 * under the app's own correct banner saying the wallet was not open yet.
 *
 * It is the returning customer who lands there: `AuthPage` sends registration
 * to `/welcome` and login to `/`, so somebody who signed up, stopped, and came
 * back the next day skipped the screen that explains where they stand and was
 * dropped into a fabricated wallet instead.
 *
 * # This is a routing default, NOT a client-side gate
 *
 * `Welcome` already carries the note that "Go to the wallet anyway" is not a
 * loophole: the frontend does not enforce onboarding, the backend refuses every
 * money-adjacent instruction through `authz.Authorize` regardless (invariant 8),
 * and a client-side block would be an authorization decision in a browser
 * bundle. All of that still holds. Nothing here blocks anything — `?anyway=1`
 * reaches `Overview` unconditionally, from any state, including one we cannot
 * read. It is a query parameter rather than router state so that it survives a
 * reload and can be linked to.
 *
 * # Why an unreadable state redirects too
 *
 * The issue's own acceptance criteria said it should not, on the grounds that
 * bouncing somebody whose state we cannot read is the `#22` mistake. On
 * reflection that is backwards, and the criterion was corrected rather than
 * followed: `#22` was about ASSERTING A CAUSE we had not verified. `Welcome`
 * asserts nothing — it says "we cannot tell you where your checks stand", offers
 * "Check again", and offers the wallet anyway. `Overview` in that same state
 * shows invented money. Sending someone to the honest screen is not stranding
 * them; the escape is one click and always present.
 *
 * So `Overview` is the default only for a customer who is actually onboarded.
 * Every other state goes to the screen built to explain it.
 */
export function WalletHome() {
  const { state } = useOnboarding()
  const [params] = useSearchParams()

  if (params.get('anyway') === '1') return <Overview />
  // Not `Overview` while loading: a flash of fabricated balances is the whole
  // defect, briefly.
  if (state.status === 'loading') return null
  if (state.status === 'ready' && state.onboarding.state === 'accepted') return <Overview />
  return <Navigate to="/welcome" replace />
}

export function App() {
  return (
    /*
      The outermost boundary (#95), INSIDE ThemeProvider so the fallback paints
      from the same custom properties as everything else, and OUTSIDE
      ConfigProvider and SessionProvider so it still catches when one of those is
      the thing that threw. Without it a render error anywhere is a blank white
      page — React unmounts the whole tree, and there is nothing below this to
      catch it.
    */
    <ThemeProvider>
      <ErrorBoundary scope="app">
        {/* Config first: SessionProvider needs the Ory URL, and neither may be
            guessed. ConfigProvider renders nothing until /config.json validates. */}
        <ConfigProvider>
          <BrowserRouter>
            <SessionProvider>
              <Routes>
                {/* Unauthenticated. All four are the same Ory flow renderer. */}
                <Route path="/sign-in" element={<AuthPage kind="login" />} />
                <Route path="/sign-up" element={<AuthPage kind="registration" />} />
                <Route path="/recover" element={<AuthPage kind="recovery" />} />
                <Route path="/verify" element={<AuthPage kind="verification" />} />

                {/* Signed in. Onboarding state is available from here inward. */}
                <Route
                  element={
                    <RequireSession>
                      <OnboardingProvider>
                        <AppShell />
                      </OnboardingProvider>
                    </RequireSession>
                  }
                >
                  <Route index element={<WalletHome />} />
                  <Route path="balances" element={<Balances />} />
                  <Route path="payments" element={<Payments />} />
                  <Route path="cards" element={<Placeholder title="Cards" />} />
                  <Route path="people" element={<People />} />
                  <Route path="earnings" element={<Placeholder title="Earnings" />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="*" element={<Placeholder title="Not found" />} />
                </Route>

                {/* Signed in, but between an account and a usable wallet. Outside
                    the shell: the rail's destinations are not reachable yet. */}
                <Route
                  path="/welcome"
                  element={
                    <RequireSession>
                      <OnboardingProvider>
                        <Welcome />
                      </OnboardingProvider>
                    </RequireSession>
                  }
                />
              </Routes>
            </SessionProvider>
          </BrowserRouter>
        </ConfigProvider>
      </ErrorBoundary>
    </ThemeProvider>
  )
}
